import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  MenuItem,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import { tokens } from "../../theme/tokens";
import { Formik } from "formik";
import * as yup from "yup";
import Header from "../../components/Header";
import { GraphQLClient } from "graphql-request";
import { graphqlEndpoint } from "../../config";

// Graph
import { CREATE_USER } from "../../graph/users/mutations";
import { CREATE_TASK } from "../../graph/tasks/mutations";
import useGraphQLClient from "../../hooks/useGraphQLClient";

const Contact = () => {
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const [formSubmitted, setFormSubmitted] = useState(false);

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const gqlClient = useGraphQLClient();

  const handleFormSubmit = async (values, { resetForm }) => {
    const client = new GraphQLClient(graphqlEndpoint);

    const interestSummary = `
Business Name: ${values.businessName}
Business Type: ${values.businessType}
Role: ${values.role}
Operational Challenge: ${values.operationalChallenge}
Current Systems: ${values.currentSystems}
Preferred Contact: ${values.preferredContact}
Referral Source: ${values.referralSource}
    `;

    const input = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      password: "temp-password",
      role: "INTERESTED",
      invitedBy: null,

      // Reusing existing user fields until schema is renamed
      interestReason: interestSummary,
      experienceLevel: values.businessType,
      twitterHandle: "",
      referralSource: values.referralSource,
    };

    try {

      const taskInput = {
        title: `Operational Clarity enquiry: ${values.businessName || `${values.firstName} ${values.lastName}`}`,
        description: `New Operational Clarity contact submission.\n\n${interestSummary}\n\nEmail: ${values.email}`,
        status: "inbox",
        labels: ["operational-clarity", "public-form", "lead"],
        department: "admin",
        assignedTo: null,
        dueDate: null,
        deferDate: null,
        duration: null,
        projectId: null,
      };

      await gqlClient.request(CREATE_TASK, { input: taskInput });

      resetForm();
      setFormSubmitted(true);
    } catch (error) {
      console.error("Contact submission or task creation error", error);
    }
  };

  return (
<Box sx={{ maxWidth: "900px" }}>
  <Header
    title="START A CONVERSATION"
    subtitle="Let’s explore what operational clarity could look like inside your business."
  />

  <Typography
    variant="h3"
    sx={{
      color: colors.houndGold?.[500],
      fontWeight: 700,
      mt: 3,
      mb: 2,
      lineHeight: 1.2,
    }}
  >
    When operations become clearer, everything feels lighter.
  </Typography>

  <Typography variant="body1" sx={{ mb: 2, maxWidth: "780px", lineHeight: 1.75 }}>
    Most growing businesses don’t struggle because people lack talent or effort.
    They struggle because knowledge becomes fragmented, responsibilities become
    unclear, and important processes rely too heavily on memory.
  </Typography>

  <Typography variant="body1" sx={{ mb: 3, maxWidth: "780px", lineHeight: 1.75 }}>
    Operational Clarity helps businesses create structure, visibility, and
    consistency - so teams can move with greater confidence and less friction.
  </Typography>

  <Box
    sx={{
      bgcolor: "background.paper",
      border: `1px solid ${colors.scalpelTeal?.[800]}`,
      borderRadius: 2,
      p: 2.5,
      mb: 3,
      maxWidth: "780px",
    }}
  >
    <Typography
      variant="h5"
      sx={{
        color: colors.scalpelTeal?.[400],
        fontWeight: 700,
        mb: 1.5,
      }}
    >
      This may be useful if you are noticing:
    </Typography>

    {[
      "Important knowledge trapped in people’s heads",
      "Processes followed differently by different people",
      "Operational bottlenecks slowing the team down",
      "Uncertainty around ownership, responsibilities, or handoffs",
      "Growth creating complexity faster than systems can support",
    ].map((item) => (
      <Box
        key={item}
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1.2,
          mb: 1,
        }}
      >
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            bgcolor: colors.houndGold?.[500],
            mt: "9px",
            flexShrink: 0,
          }}
        />

        <Typography
          variant="body2"
          sx={{
            color: "text.primary",
            lineHeight: 1.6,
          }}
        >
          {item}
        </Typography>
      </Box>
    ))}
  </Box>

  <Typography
    variant="h5"
    sx={{
      fontWeight: 700,
      color: colors.scalpelTeal?.[400],
      mb: 2,
    }}
  >
    If that sounds familiar, let’s start there.
  </Typography>


      {formSubmitted ? (
        <Box mt={4} textAlign="center">
          <Typography
            variant="h4"
            gutterBottom
            sx={{ color: colors.houndGold?.[500] }}
          >
            Thanks - I’ll Be in Touch
          </Typography>

          <Typography variant="body1" mb={2}>
            Thanks for reaching out. I’ll review what you’ve shared and come
            back to you shortly.
          </Typography>

          <Typography variant="body2" color="textSecondary">
            In the meantime, you can return to the homepage and explore the
            current direction for Operational Clarity.
          </Typography>

          <Button variant="outlined" color="secondary" href="/" sx={{ mt: 3 }}>
            Return Home
          </Button>
        </Box>
      ) : (
        <Formik
          onSubmit={handleFormSubmit}
          initialValues={initialValues}
          validationSchema={contactSchema}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
            isSubmitting,
          }) => (
            <form onSubmit={handleSubmit}>
              <Box
                display="grid"
                gap="30px"
                gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                sx={{
                  "& > div": {
                    gridColumn: isNonMobile ? undefined : "span 4",
                  },
                }}
              >
                <TextField
                  fullWidth
                  variant="filled"
                  label="First Name"
                  name="firstName"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.firstName}
                  error={!!touched.firstName && !!errors.firstName}
                  helperText={touched.firstName && errors.firstName}
                  sx={{ gridColumn: "span 2" }}
                />

                <TextField
                  fullWidth
                  variant="filled"
                  label="Last Name"
                  name="lastName"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.lastName}
                  error={!!touched.lastName && !!errors.lastName}
                  helperText={touched.lastName && errors.lastName}
                  sx={{ gridColumn: "span 2" }}
                />

                <TextField
                  fullWidth
                  variant="filled"
                  label="Email"
                  name="email"
                  type="email"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.email}
                  error={!!touched.email && !!errors.email}
                  helperText={touched.email && errors.email}
                  sx={{ gridColumn: "span 4" }}
                />

                <TextField
                  fullWidth
                  variant="filled"
                  label="Business Name"
                  name="businessName"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.businessName}
                  error={!!touched.businessName && !!errors.businessName}
                  helperText={touched.businessName && errors.businessName}
                  sx={{ gridColumn: "span 2" }}
                />

                <TextField
                  fullWidth
                  variant="filled"
                  label="Your Role"
                  name="role"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.role}
                  error={!!touched.role && !!errors.role}
                  helperText={touched.role && errors.role}
                  sx={{ gridColumn: "span 2" }}
                />

                <TextField
                  select
                  fullWidth
                  variant="filled"
                  label="Business Type"
                  name="businessType"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.businessType}
                  error={!!touched.businessType && !!errors.businessType}
                  helperText={touched.businessType && errors.businessType}
                  sx={{ gridColumn: "span 4" }}
                >
                  <MenuItem value="BREWERY">Independent Brewery</MenuItem>
                  <MenuItem value="HOSPITALITY">Hospitality / Venue</MenuItem>
                  <MenuItem value="PRODUCTION">Production Business</MenuItem>
                  <MenuItem value="RETAIL">Retail / Distribution</MenuItem>
                  <MenuItem value="OTHER">Other</MenuItem>
                </TextField>

                <TextField
                  fullWidth
                  variant="filled"
                  label="Where does your operation feel unclear or harder than it should?"
                  name="operationalChallenge"
                  multiline
                  minRows={4}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.operationalChallenge}
                  error={
                    !!touched.operationalChallenge &&
                    !!errors.operationalChallenge
                  }
                  helperText={
                    touched.operationalChallenge &&
                    errors.operationalChallenge
                  }
                  sx={{ gridColumn: "span 4" }}
                />

                <TextField
                  fullWidth
                  variant="filled"
                  label="What tools or systems are you currently using? (optional)"
                  name="currentSystems"
                  multiline
                  minRows={2}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.currentSystems}
                  sx={{ gridColumn: "span 4" }}
                />

                <TextField
                  select
                  fullWidth
                  variant="filled"
                  label="Preferred Contact Method"
                  name="preferredContact"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.preferredContact}
                  error={
                    !!touched.preferredContact &&
                    !!errors.preferredContact
                  }
                  helperText={
                    touched.preferredContact && errors.preferredContact
                  }
                  sx={{ gridColumn: "span 4" }}
                >
                  <MenuItem value="EMAIL">Email</MenuItem>
                  <MenuItem value="PHONE">Phone</MenuItem>
                  <MenuItem value="VIDEO_CALL">Video Call</MenuItem>
                  <MenuItem value="IN_PERSON">In Person / Local Meet</MenuItem>
                </TextField>

                <TextField
                  fullWidth
                  variant="filled"
                  label="How did you hear about Operational Clarity? (optional)"
                  name="referralSource"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.referralSource}
                  sx={{ gridColumn: "span 4" }}
                />
              </Box>

              <Box display="flex" justifyContent="end" mt="20px">
                <Button
                  type="submit"
                  color="secondary"
                  variant="contained"
                  disabled={isSubmitting}
                >
                  Send Enquiry
                </Button>
              </Box>
            </form>
          )}
        </Formik>
      )}
    </Box>
  );
};

const contactSchema = yup.object().shape({
  firstName: yup.string().required("First name is required"),
  lastName: yup.string().required("Last name is required"),
  email: yup.string().email("Invalid email").required("Email is required"),
  businessName: yup.string().required("Business name is required"),
  role: yup.string().required("Your role is required"),
  businessType: yup
    .string()
    .oneOf(["BREWERY", "HOSPITALITY", "PRODUCTION", "RETAIL", "OTHER"])
    .required("Select your business type"),
  operationalChallenge: yup
    .string()
    .required("Please describe the operational challenge"),
  currentSystems: yup.string(),
  preferredContact: yup
    .string()
    .oneOf(["EMAIL", "PHONE", "VIDEO_CALL", "IN_PERSON"])
    .required("Select your preferred contact method"),
  referralSource: yup.string(),
});

const initialValues = {
  firstName: "",
  lastName: "",
  email: "",
  businessName: "",
  role: "",
  businessType: "",
  operationalChallenge: "",
  currentSystems: "",
  preferredContact: "",
  referralSource: "",
};

export default Contact;